import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Modal } from './Modal';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { ImageCrop } from '../../services/adminService';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

interface ImageCropModalProps {
  file: File;
  aspect: number;
  title: string;
  onConfirm: (crop: ImageCrop) => void;
  onCancel: () => void;
  onSkip: () => void;
  allowAuto?: boolean;
}

function toImageCrop(area: Area): ImageCrop {
  return {
    x: Math.round(area.x),
    y: Math.round(area.y),
    width: Math.round(area.width),
    height: Math.round(area.height),
  };
}

function ZoomOutIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m20 20-3.5-3.5M8 11h6" />
    </svg>
  );
}

function ZoomInIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m20 20-3.5-3.5M8 11h6M11 8v6" />
    </svg>
  );
}

export function ImageCropModal({ file, aspect, title, onConfirm, onCancel, onSkip, allowAuto }: ImageCropModalProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const areaRef = useRef<Area | null>(null);
  const skipRef = useRef(onSkip);
  skipRef.current = onSkip;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => setImageUrl(url);
    probe.onerror = () => skipRef.current();
    probe.src = url;
    return () => {
      probe.onload = null;
      probe.onerror = null;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const handleCropComplete = useCallback((_: Area, areaPixels: Area) => {
    areaRef.current = areaPixels;
  }, []);

  const handleConfirm = () => {
    if (areaRef.current) onConfirm(toImageCrop(areaRef.current));
  };

  return (
    <Modal
      open
      stacked
      onClose={onCancel}
      title={title}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancelar
          </Button>
          {allowAuto && (
            <Button type="button" variant="outline" size="sm" onClick={onSkip}>
              Usar recorte automático
            </Button>
          )}
          <Button type="button" size="sm" disabled={!imageUrl} onClick={handleConfirm}>
            Recortar e enviar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="relative w-full h-[55vh] max-h-[420px] min-h-[240px] rounded-lg overflow-hidden bg-text-dark">
          {imageUrl ? (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
              objectFit="contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner size="md" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 text-chocolate">
          <ZoomOutIcon />
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={zoom}
            aria-label="Zoom"
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-8 accent-chocolate cursor-pointer"
          />
          <ZoomInIcon />
        </div>
        <p className="text-xs text-text-medium">
          Arraste a imagem para posicionar e use o zoom (ou o gesto de pinça) para ajustar o enquadramento.
        </p>
      </div>
    </Modal>
  );
}

interface CropRequest {
  file: File;
  aspect: number;
  title: string;
  allowAuto: boolean;
}

export type CropOutcome = { cancelled: true } | { cancelled: false; crop?: ImageCrop };

export function useImageCrop() {
  const [request, setRequest] = useState<CropRequest | null>(null);
  const resolveRef = useRef<((outcome: CropOutcome) => void) | null>(null);

  const requestCrop = useCallback((file: File, aspect: number, title: string, allowAuto = false) => {
    return new Promise<CropOutcome>((resolve) => {
      resolveRef.current = resolve;
      setRequest({ file, aspect, title, allowAuto });
    });
  }, []);

  const settle = useCallback((outcome: CropOutcome) => {
    setRequest(null);
    resolveRef.current?.(outcome);
    resolveRef.current = null;
  }, []);

  const ImageCropElement = request ? (
    <ImageCropModal
      file={request.file}
      aspect={request.aspect}
      title={request.title}
      allowAuto={request.allowAuto}
      onConfirm={(crop) => settle({ cancelled: false, crop })}
      onCancel={() => settle({ cancelled: true })}
      onSkip={() => settle({ cancelled: false })}
    />
  ) : null;

  return { requestCrop, ImageCropElement };
}

const PREVIEW_MAX_SIZE = 320;

function loadImage(url: string) {
  return new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function centerCrop(width: number, height: number, aspect: number): ImageCrop {
  if (width / height > aspect) {
    const cropWidth = Math.round(height * aspect);
    return { x: Math.round((width - cropWidth) / 2), y: 0, width: cropWidth, height };
  }
  const cropHeight = Math.round(width / aspect);
  return { x: 0, y: Math.round((height - cropHeight) / 2), width, height: cropHeight };
}

export async function createCropPreview(file: File, crop: ImageCrop | undefined, aspect: number): Promise<string | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    if (!img) return null;
    const area = crop ?? centerCrop(img.naturalWidth, img.naturalHeight, aspect);
    if (area.width <= 0 || area.height <= 0) return null;
    const scale = Math.min(1, PREVIEW_MAX_SIZE / Math.max(area.width, area.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(area.width * scale));
    canvas.height = Math.max(1, Math.round(area.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
